import React from "react";
import {
  Autocomplete,
  Checkbox,
  ListItemIcon,
  ListItemText,
  TextField,
} from "@mui/material";
import { FixedSizeList as List } from "react-window";

const ITEM_HEIGHT = 48;
const DEFAULT_MAX_HEIGHT = 360;
const SELECT_ALL_VALUE = "all";

const VirtualizedConfigContext = React.createContext({
  itemSize: ITEM_HEIGHT,
  maxHeight: DEFAULT_MAX_HEIGHT,
});

const VirtualizedListbox = React.forwardRef(function VirtualizedListbox(
  props,
  ref,
) {
  const { children, className, ...other } = props;
  const { itemSize, maxHeight } = React.useContext(VirtualizedConfigContext);

  const optionChildren = React.Children.toArray(children);
  const listHeight = Math.min(optionChildren.length * itemSize, maxHeight);

  return (
    <div ref={ref} className={className} {...other}>
      <List
        height={listHeight}
        width="100%"
        itemCount={optionChildren.length}
        itemSize={itemSize}
        innerElementType="ul"
      >
        {({ index, style }) => {
          const child = optionChildren[index];
          return React.cloneElement(child, {
            style: { ...style, ...child.props.style },
          });
        }}
      </List>
    </div>
  );
});

export default function VirtualizedMultiSelect({
  label,
  id,
  value,
  onChange,
  options = [],
  disabled = false,
  width,
  minWidth = 160,
  marginTop = 2,
  paperWidth,
  showSelectAll = false,
  selectAllChecked,
  selectAllIndeterminate,
  selectAllText = "Select All",
  formControlSx,
  itemSize = ITEM_HEIGHT,
  maxHeight = DEFAULT_MAX_HEIGHT,
}) {
  const currentValue = value || [];
  const displayValue = currentValue.filter((item) => item !== SELECT_ALL_VALUE);
  const [inputValue, setInputValue] = React.useState("");

  const allSelected = Boolean(
    options.length > 0 && displayValue.length === options.length,
  );
  const partiallySelected = Boolean(
    displayValue.length > 0 && displayValue.length < options.length,
  );
  const isAllChecked =
    selectAllChecked !== undefined ? selectAllChecked : allSelected;
  const isAllIndeterminate =
    selectAllIndeterminate !== undefined
      ? selectAllIndeterminate
      : partiallySelected;

  const handleChange = (_event, newValue) => {
    onChange?.({ target: { value: newValue || [] } });
    // keep typed filter visible so picking multiple matches doesn't require re-typing
  };

  const handleInputChange = (_event, newInputValue, reason) => {
    if (reason === "input") {
      setInputValue(newInputValue);
    } else if (reason === "clear") {
      setInputValue("");
    }
    // ignore "reset" (selection) to preserve filter text
  };

  const filterOptions = (_, state) => {
    const filterText = state.inputValue ?? inputValue;
    const filtered = options.filter((item) =>
      item.toLowerCase().includes(filterText.toLowerCase()),
    );
    return showSelectAll ? [SELECT_ALL_VALUE, ...filtered] : filtered;
  };

  return (
    <VirtualizedConfigContext.Provider value={{ itemSize, maxHeight }}>
      <Autocomplete
        id={id}
        multiple
        disableCloseOnSelect
        limitTags={3}
        disabled={disabled}
        options={showSelectAll ? [SELECT_ALL_VALUE, ...options] : options}
        value={displayValue}
        onChange={handleChange}
        inputValue={inputValue}
        onInputChange={handleInputChange}
        filterOptions={filterOptions}
        getOptionLabel={(option) =>
          option === SELECT_ALL_VALUE ? selectAllText : option
        }
        renderOption={(props, option) => {
          if (option === SELECT_ALL_VALUE) {
            return (
              <li {...props} key={SELECT_ALL_VALUE}>
                <ListItemIcon>
                  <Checkbox
                    checked={isAllChecked}
                    indeterminate={isAllIndeterminate}
                  />
                </ListItemIcon>
                <ListItemText primary={selectAllText} />
              </li>
            );
          }
          return (
            <li {...props} key={option}>
              <Checkbox checked={displayValue.indexOf(option) > -1} />
              <ListItemText primary={option} />
            </li>
          );
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            label={label}
            size="small"
            sx={formControlSx}
          />
        )}
        ListboxComponent={VirtualizedListbox}
        sx={{ width, minWidth, marginTop }}
      />
    </VirtualizedConfigContext.Provider>
  );
}